import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { Subject, debounceTime, firstValueFrom, take } from 'rxjs';

import {
  StagedWidgetLayout,
  applyStagedLayout,
} from 'src/app/shared/utils/widget-list.utils';

import { objectChanged } from '../actions/core.actions';
import { feedUnloaded } from '../actions/feed.actions';
import { selectDashboardPage } from '../actions/object.actions';
import { selectWidgetSearchString } from '../reducers';
import { CurrentViewWidgetsService } from './current-view-widgets.service';
import { WidgetPersistenceService } from './widget-persistence.service';

export interface StagedWidgetLayoutEntry extends StagedWidgetLayout {
  widgetId: string;
}

/** How long resize/reorder events accumulate before they are persisted. */
const LAYOUT_FLUSH_DEBOUNCE_MS = 1000;

/**
 * Un-persisted widget geometry, held between a gridstack drag/resize and the PATCH
 * that saves it. The views merge `layout()` over their list query in a computed,
 * so the grid and the PATCH payload see the same positions.
 *
 * An overlay rather than an optimistic `setQueryData`: a mid-drag refetch would
 * land on top of pending edits, and a wrong-key layout patch is exactly how
 * WAP-874's layout corruption happened. An overlay is refetch-proof and cannot
 * reach Presentation Mode's cache entry at all; its worst failure is a stale
 * overlay rather than silently persisted corruption.
 *
 * **The overlay is deliberately NOT cleared when the flush succeeds** - only when
 * the view changes. Clearing on success caused a request avalanche: the overlay is
 * a signal (clears synchronously) while the query's `data()` arrives a microtask
 * later, so for one tick the computed saw old cache + empty overlay, gridstack
 * moved the widget back and `changeCB` PATCHed the old geometry, then the pending
 * notification moved it forward again. Leaving the entry in place closes that
 * window; once the flush has written the same values into the cache the overlay is
 * inert, because `applyStagedLayout` returns matching widgets by identity.
 */
@Injectable({ providedIn: 'root' })
export class WidgetLayoutStagingService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentViewWidgets = inject(CurrentViewWidgetsService);
  private readonly persistence = inject(WidgetPersistenceService);

  private readonly pending = signal<ReadonlyMap<string, StagedWidgetLayout>>(
    new Map(),
  );
  private readonly flushRequested$ = new Subject<void>();

  private inFlight = false;
  private flushAgain = false;
  /** Bumped whenever the rendered view changes; see the reset subscription. */
  private viewToken = 0;

  /** Merge this over the list query's data before rendering it. */
  readonly layout = this.pending.asReadonly();

  constructor() {
    this.flushRequested$
      .pipe(
        debounceTime(LAYOUT_FLUSH_DEBOUNCE_MS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.flush());

    // * without this reset, a drag staged while a search filter blocks the flush
    // * would survive into the next view
    this.actions$
      .pipe(
        ofType(objectChanged, selectDashboardPage, feedUnloaded),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.pending.set(new Map());
        // ! invalidates an in-flight flush: its cache write would otherwise land
        // ! on the *new* view's list entry, which is how WAP-874 happened
        this.viewToken++;
      });
  }

  /** Gridstack reported new positions (drag / resize). Persisted immediately. */
  stageReposition(entries: StagedWidgetLayoutEntry[]): void {
    this.merge(entries);
    this.flush();
  }

  /** The edit-mode widget list was reordered. Debounced before it is persisted. */
  stageResize(entries: StagedWidgetLayoutEntry[]): void {
    this.merge(entries);
    this.flushRequested$.next();
  }

  private merge(entries: StagedWidgetLayoutEntry[]): void {
    if (!entries.length) return;

    const next = new Map(this.pending());
    for (const { widgetId, x, y, cols, rows } of entries) {
      next.set(widgetId, { x, y, cols, rows });
    }

    this.pending.set(next);
  }

  private async flush(): Promise<void> {
    // * one request at a time: gridstack fires `changeCB` several times during a
    // * single drag, and the payload is the *whole* list, so overlapping PATCHes
    // * would be redundant by construction. The trailing re-flush below sends
    // * whatever was staged while this one was in flight.
    if (this.inFlight) {
      this.flushAgain = true;
      return;
    }

    const staged = this.pending();
    if (!staged.size) return;

    const cached = this.currentViewWidgets.snapshot();
    const widgets = applyStagedLayout(cached, staged);

    // * nothing the cache doesn't already hold (`applyStagedLayout` returns its
    // * input untouched then). Gridstack re-announces geometry it was *given* -
    // * every `grid.update()` can produce a `changeCB` - so without this a single
    // * echo would turn into a PATCH, and its response into another echo.
    if (widgets === cached) return;

    // ! the whole list is PATCHed, so persisting while a search filter is active
    // ! would save positions gridstack computed for a *subset*
    const searchString = await firstValueFrom(
      this.store.select(selectWidgetSearchString).pipe(take(1)),
    );
    if (searchString?.length) return;

    this.inFlight = true;
    const viewToken = this.viewToken;

    try {
      await this.persistence.updateWidgetsOrder(widgets);

      // * the server accepted these positions, so they belong in the cache -
      // * unless the user has since left the view, in which case
      // * `CurrentViewWidgetsService` now resolves a *different* list entry
      if (viewToken === this.viewToken) {
        this.currentViewWidgets.applyLayout(staged);
      }
    } catch {
      // * silent by design: the overlay stays, so the user keeps seeing where
      // * they dropped the widget
    } finally {
      this.inFlight = false;
    }

    if (this.flushAgain) {
      this.flushAgain = false;
      if (viewToken === this.viewToken) {
        this.flush();
      }
    }
  }
}
